import React, { useState } from "react";
import api, { apiError, formatINR } from "@/lib/api";
import { Modal, Input, Select, PrimaryButton, GhostButton } from "@/components/common";
import { StatusBadge } from "@/components/StatusBadge";
import { Check, X, Trash2, Lock } from "lucide-react";
import { toast } from "sonner";

export function BlockTimeModal({ open, onClose, units, initial, onSaved }) {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  React.useEffect(() => { if (open) { setF(initial); } }, [open, initial]);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/facilities/units/${f.unit_id}/blocks`, { date: f.date, start: f.start, end: f.end, reason: f.reason });
      toast.success("Time blocked"); onSaved();
    } catch (err) { toast.error(apiError(err)); }
    setBusy(false);
  };
  return (
    <Modal open={open} onClose={onClose} title="Block time">
      <form onSubmit={submit} className="space-y-4" data-testid="block-time-form">
        <Select label="Unit" testId="block-unit" required value={f.unit_id} onChange={(e) => setF({ ...f, unit_id: e.target.value })}>
          {units.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </Select>
        <Input label="Date" testId="block-date" type="date" required value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} />
        <div className="grid grid-cols-2 gap-3">
          <Input label="Start" testId="block-start" type="time" required value={f.start} onChange={(e) => setF({ ...f, start: e.target.value })} />
          <Input label="End" testId="block-end" type="time" required value={f.end} onChange={(e) => setF({ ...f, end: e.target.value })} />
        </div>
        <Select label="Reason" testId="block-reason" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })}>
          <option value="maintenance">Maintenance</option><option value="private event">Private event</option>
          <option value="tournament">Tournament</option><option value="closed">Closed</option>
        </Select>
        <p className="text-xs text-muted-foreground">Blocked time can't overlap existing confirmed or held bookings.</p>
        <div className="flex justify-end gap-2">
          <GhostButton type="button" onClick={onClose}>Cancel</GhostButton>
          <PrimaryButton data-testid="block-save-btn" type="submit" disabled={busy}><Lock className="h-4 w-4" /> Block time</PrimaryButton>
        </div>
      </form>
    </Modal>
  );
}

export function EventDetailModal({ ev, unitName, onClose, onChanged, onDecide }) {
  if (!ev) return null;
  const isBlock = ev.kind === "block";
  const remove = async () => {
    try { await api.delete(`/facilities/blocks/${ev.id}`); toast.success("Block removed"); onChanged(); }
    catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Modal open onClose={onClose} title={isBlock ? "Blocked time" : `Booking ${ev.reference || ""}`}>
      <div className="space-y-2 text-sm" data-testid="event-detail">
        <p><span className="text-muted-foreground">Unit:</span> {unitName}</p>
        <p><span className="text-muted-foreground">When:</span> {ev.date} · {ev.start}–{ev.end}</p>
        {isBlock ? <p className="capitalize"><span className="text-muted-foreground">Reason:</span> {ev.reason}</p> : (
          <>
            <p><span className="text-muted-foreground">Booked by:</span> {ev.user_name}</p>
            <p><span className="text-muted-foreground">Amount:</span> {formatINR(ev.amount)}</p>
            <div><StatusBadge status={ev.status} /></div>
          </>
        )}
      </div>
      <div className="mt-5 flex justify-end gap-2">
        {isBlock && <GhostButton data-testid="block-delete-btn" onClick={remove}><Trash2 className="h-4 w-4" /> Remove block</GhostButton>}
        {!isBlock && ev.status === "pending_approval" && <DecisionButtons b={ev} onDecide={onDecide} />}
        <GhostButton onClick={onClose}>Close</GhostButton>
      </div>
    </Modal>
  );
}

export function DecisionButtons({ b, onDecide }) {
  return (
    <div className="flex gap-2">
      <button data-testid={`approve-btn-${b.id}`} onClick={() => onDecide(b, "approve")}
        className="inline-flex items-center gap-1 rounded-lg bg-green-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-green-700">
        <Check className="h-3.5 w-3.5" /> Approve
      </button>
      <button data-testid={`reject-btn-${b.id}`} onClick={() => onDecide(b, "reject")}
        className="inline-flex items-center gap-1 rounded-lg border border-rose-300 px-3 py-1.5 text-xs font-semibold text-rose-600 transition-colors hover:bg-rose-50">
        <X className="h-3.5 w-3.5" /> Reject
      </button>
    </div>
  );
}

export function RejectModal({ booking, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  if (!booking) return null;
  return (
    <Modal open onClose={onClose} title="Reject booking request">
      <p className="mb-3 text-sm text-muted-foreground">{booking.user_name} · {booking.date} {booking.start}–{booking.end}</p>
      <Input label="Reason (shared with the requester)" testId="reject-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Ground unavailable" />
      <div className="mt-4 flex justify-end gap-2">
        <GhostButton onClick={onClose}>Cancel</GhostButton>
        <PrimaryButton data-testid="reject-confirm-btn" className="bg-rose-600 hover:bg-rose-700" onClick={() => onConfirm(reason)}>Reject</PrimaryButton>
      </div>
    </Modal>
  );
}
